import { CalendarDays, MapPin, PenSquare, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../../api/client";
import EventEditor from "../../../components/admin/EventEditor";
import LoadingState from "../../../components/common/LoadingState";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { useToast } from "../../../context/ToastContext";
import type { EventItem } from "../../../types/api";
import { formatCompactDate, formatCurrency } from "../../../utils/format";

const statusBadge: Record<string, string> = {
  draft: "ego-badge ego-badge-neutral",
  published: "ego-badge ego-badge-success",
  "sold-out": "ego-badge ego-badge-warning",
  completed: "ego-badge ego-badge-info",
};

function badgeFor(status?: string) {
  if (!status) return "ego-badge ego-badge-neutral";
  return statusBadge[status.toLowerCase()] ?? "ego-badge ego-badge-neutral";
}

export default function AdminEventsPage() {
  const { token } = useAdminAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventItem[]>([]);
  const [editingEvent, setEditingEvent] = useState<EventItem | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | EventItem["status"]>("all");

  const loadEvents = async () => {
    if (!token) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await adminApi.listEvents(token);
      setEvents(response.events);
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Could not load screenings";
      setError(message);
      showToast(message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadEvents();
  }, [token]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return events.filter((event) => {
      if (statusFilter !== "all" && event.status !== statusFilter) return false;
      if (!term) return true;
      return (
        event.title.toLowerCase().includes(term) ||
        event.city.toLowerCase().includes(term) ||
        event.category.toLowerCase().includes(term)
      );
    });
  }, [events, query, statusFilter]);

  if (isLoading) return <LoadingState label="Loading screenings..." />;

  return (
    <div className="space-y-6">
      <div className="ego-surface flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa3b2]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by title, city or category"
              className="ego-input pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-1 rounded-xl bg-surface-muted p-1">
            {(["all", "published", "draft", "sold-out", "completed"] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatusFilter(value)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold capitalize transition ${
                  statusFilter === value
                    ? "bg-surface text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {value === "all" ? "All" : value.replace("-", " ")}
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditingEvent(null);
            setIsEditorOpen(true);
          }}
          className="ego-btn-primary"
        >
          <Plus size={16} /> New screening
        </button>
      </div>

      {error && (
        <div className="ego-surface border-l-4 border-l-red-500 px-5 py-4 text-sm font-semibold text-red-600">
          {error}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="ego-surface px-6 py-16 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]">
            <CalendarDays size={20} />
          </div>
          <h3 className="text-lg font-bold text-foreground">No screenings match</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
            Adjust your search or status filter, or create a new screening to get started.
          </p>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2 2xl:grid-cols-3">
          {filtered.map((event) => {
            const total = event.stats.totalSold + event.stats.available;
            const sellThrough = total > 0 ? Math.round((event.stats.totalSold / total) * 100) : 0;
            return (
              <article
                key={event.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/admin/screenings/${event.id}`)}
                onKeyDown={(keyEvent) => {
                  if (keyEvent.key === "Enter" || keyEvent.key === " ") {
                    keyEvent.preventDefault();
                    navigate(`/admin/screenings/${event.id}`);
                  }
                }}
                className="ego-surface group cursor-pointer overflow-hidden transition hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(11,18,32,0.25)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pp-gold)]"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                  <div className="absolute left-4 top-4 flex gap-2">
                    <span className="ego-badge bg-white/95 text-[#0b1220]">{event.category}</span>
                    {event.featured && <span className="ego-badge ego-badge-brand">Featured</span>}
                  </div>
                  <div className="absolute right-4 top-4">
                    <div className="flex flex-col items-end gap-2">
                      <span className={badgeFor(event.status)}>{event.status}</span>
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <div className="text-base font-bold leading-tight">{event.title}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-white/85">
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={12} /> {event.city}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays size={12} /> {formatCompactDate(event.date)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 p-5">
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <div className="ego-eyebrow">From</div>
                      <div className="mt-1 text-lg font-bold text-foreground">
                        {formatCurrency(event.stats.minPrice)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="ego-eyebrow">Sell-through</div>
                      <div className="mt-1 text-lg font-bold text-foreground">{sellThrough}%</div>
                    </div>
                  </div>

                  <div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-surface-muted">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[var(--pp-gold)] to-[#ffb084]"
                        style={{ width: `${Math.min(sellThrough, 100)}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{event.stats.totalSold} sold</span>
                      <span>{event.stats.available} available</span>
                      <span>{event.ticketTiers.length} tiers</span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={(buttonEvent) => {
                        buttonEvent.stopPropagation();
                        setEditingEvent(event);
                        setIsEditorOpen(true);
                      }}
                      className="ego-btn-primary flex-1"
                    >
                      <PenSquare size={14} /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={async (buttonEvent) => {
                        buttonEvent.stopPropagation();
                        if (!token) return;
                        const confirmed = window.confirm(`Delete screening "${event.title}"?`);
                        if (!confirmed) return;
                        try {
                          await adminApi.deleteEvent(token, event.id);
                          showToast(`Deleted screening "${event.title}"`, "success");
                          await loadEvents();
                        } catch (requestError) {
                          const message =
                            requestError instanceof Error ? requestError.message : "Could not delete screening";
                          setError(message);
                          showToast(message, "error");
                        }
                      }}
                      className="ego-btn-danger"
                      aria-label="Delete screening"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <EventEditor
        event={editingEvent}
        isOpen={isEditorOpen}
        isSaving={isSaving}
        onClose={() => setIsEditorOpen(false)}
        onSubmit={async (payload) => {
          if (!token) return;
          setIsSaving(true);
          setError(null);
          const isUpdate = Boolean(editingEvent?.id);
          try {
            await adminApi.saveEvent(token, payload, editingEvent?.id);
            setIsEditorOpen(false);
            setEditingEvent(null);
            const message = isUpdate ? "Screening updated" : "Screening created";
            showToast(message, "success");
            await loadEvents();
          } catch (requestError) {
            const message =
              requestError instanceof Error ? requestError.message : "Could not save screening";
            setError(message);
            showToast(message, "error");
          } finally {
            setIsSaving(false);
          }
        }}
      />
    </div>
  );
}
